import { useState } from 'react';
import { Button, StateView, useToast } from '@hana/ui';
import { useT } from '@hana/i18n';
import { IconAlertTriangle, IconCalendarOff, IconCalendarPlus, IconSwitchHorizontal, IconX } from '@tabler/icons-react';
import { LTS_WEEKS, cellView, delayCount, hasChange, nowDelays, parseCell } from './lts';
import { BOARD_LTS } from './servicesBoardData';
import { needsSchedule, type ServiceRow } from './svcmap';
import './LtsTab.i18n';
import s from './servicesmap.module.css';

const POLS = [['all', 'lt.allPorts'], ['VNSGN', 'VNSGN'], ['VNVUT', 'VNVUT'], ['VNHPH', 'VNHPH']] as const;
const SPANS = [['4w', 'lt.w4'], ['8w', 'lt.w8']] as const;
const LEGEND = [['ok', 'lt.lgOk'], ['now', 'lt.lgNow'], ['blank', 'lt.lgBlank'], ['end', 'lt.lgEnd']] as const;
// TODO: số "Đổi tàu" = chênh lệch thực tế ↔ lịch dài hạn chờ duyệt (tab Nhập & đồng bộ); chưa có nguồn nên giữ số mẫu của board.
const RECON_VESSEL_CHANGES = 1;

interface Props {
  rows: readonly ServiceRow[];
  dens: 'std' | 'gon';
  onView: (v: 'svc' | 'imp') => void;
}

export function LtsTab({ rows, dens, onView }: Props) {
  const t = useT();
  const toast = useToast();
  const [pol, setPol] = useState<string>('all');
  const [span, setSpan] = useState<'4w' | '8w'>('8w');
  const [only, setOnly] = useState(false);
  const [sel, setSel] = useState<{ code: string; i: number } | null>(null);
  const pending = () => toast({ msg: t('lt.noWrite'), tone: 'err' });

  const nW = span === '4w' ? 5 : 9;
  const cellsOf = (v: ServiceRow) => BOARD_LTS[v.code] ?? [];
  const shown = rows.filter((v) => (pol === 'all' || v.vn === pol) && (!only || hasChange(v, cellsOf(v))));
  const nBlank = rows.reduce((n, v) => n + (v.active ? v.blank : 0), 0);
  const nDue = rows.filter(needsSchedule).length;
  const nDelay = nowDelays(rows.map((v) => ({ cells: cellsOf(v) })));
  const kpis = [
    { k: t('lt.blank'), v: nBlank, sub: t('lt.blankSub'), tone: nBlank ? s.kpiWarn : undefined, go: () => setOnly(true) },
    { k: t('lt.delay'), v: nDelay, sub: t('lt.delaySub'), tone: nDelay ? s.kpiWarn : undefined, go: () => onView('imp') },
    { k: t('lt.vessel'), v: RECON_VESSEL_CHANGES, sub: t('lt.vesselSub'), tone: RECON_VESSEL_CHANGES ? s.kpiLink : undefined, go: () => onView('imp') },
    { k: t('lt.end'), v: nDue, sub: t('lt.endSub'), tone: nDue ? s.kpiErr : undefined, go: () => onView('svc') },
  ];

  const svc = sel ? rows.find((v) => v.code === sel.code) : undefined;
  const detail = (() => {
    if (!sel || !svc) return null;
    const raw = cellsOf(svc)[sel.i] ?? '-';
    const c = parseCell(raw, sel.i);
    const w = LTS_WEEKS[sel.i] ?? LTS_WEEKS[1]!;
    const dl = delayCount(c);
    const status = { ok: c.now ? [t('lt.sRan'), s.stNeutral] : [t('lt.sDeclared'), s.stOk], blank: [t('lt.blank'), s.stWarn], end: [t('lt.end'), s.stErr], nodata: [t('lt.sNodata'), s.stNeutral] }[c.kind] as [string, string];
    const notice = c.now ? (dl ? t('lt.delayN', { n: dl }) : c.flag === 'c' ? 'Closing Change' : 'Loading Confirmation') : c.flag.startsWith('+') ? t('lt.added', { v: c.flag.slice(1) }) : '—';
    const info: [string, string][] = [['ETD', c.kind === 'ok' ? `${c.etd}/2026` : '—'], [t('lt.carrier'), svc.cars.join(' · ')], [t('lt.notice'), notice], [t('lt.src'), c.now ? 'TblVesselData' : 'TblServiceRoute_Vessel']];
    const acts: { k: string; icon: typeof IconX; go: () => void }[] =
      c.kind === 'ok'
        ? [{ k: t('lt.aSwap'), icon: IconSwitchHorizontal, go: pending }, { k: t('lt.aMark'), icon: IconCalendarOff, go: pending }, { k: t('lt.aOpen'), icon: IconAlertTriangle, go: () => onView('svc') }]
        : c.kind === 'blank'
          ? [{ k: t('lt.aAdd'), icon: IconCalendarPlus, go: pending }, { k: t('lt.aNotBlank'), icon: IconCalendarOff, go: pending }]
          : [{ k: t('lt.aImport'), icon: IconCalendarPlus, go: () => onView('imp') }, { k: t('lt.aOpen'), icon: IconAlertTriangle, go: () => onView('svc') }];
    return { title: c.kind === 'ok' ? c.ves : `${svc.code} · ${w[0]}`, sub: t('lt.detSub', { code: svc.code, name: svc.name, vn: svc.vn, w: w[0], d: w[1] }), status, info, acts };
  })();

  return (
    <>
      <div className={s.kpis}>
        {kpis.map((k) => (
          <button key={k.k} type="button" className={s.kpi} title={k.sub} aria-label={`${k.k} ${k.v}`} onClick={k.go}>
            <span className={s.kpiK}>{k.k}</span>
            <b className={k.tone}>{k.v}</b>
          </button>
        ))}
      </div>
      <section className={s.panel} aria-label={t('lt.title')}>
        <div className={s.pbar}>
          <div className={s.seg} role="radiogroup" aria-label={t('lt.polAria')}>
            {POLS.map(([k, l]) => (
              <button key={k} type="button" role="radio" aria-checked={pol === k} className={s.segBtn} onClick={() => { setPol(k); setSel(null); }}>{t(l)}</button>
            ))}
          </div>
          <div className={s.seg} role="radiogroup" aria-label={t('lt.spanAria')}>
            {SPANS.map(([k, l]) => (
              <button key={k} type="button" role="radio" aria-checked={span === k} className={s.segBtn} onClick={() => { setSpan(k); setSel(null); }}>{t(l)}</button>
            ))}
          </div>
          <button type="button" className={`${s.dd} ${only ? s.ddOn : ''}`} aria-pressed={only} onClick={() => { setOnly(!only); setSel(null); }}>{t('lt.only')}</button>
          <span className={s.grow} />
          <div className={s.legend}>
            {LEGEND.map(([k, l]) => (
              <span key={k}><i className={`${s.lgBox} ${s[`lg_${k}`]}`} />{t(l)}</span>
            ))}
          </div>
        </div>
        {shown.length === 0 ? (
          <StateView state="empty" title={t('lt.noMatch')} desc={t('lt.noMatchD')} action={<Button variant="secondary" onClick={() => { setPol('all'); setOnly(false); }}>{t('lt.clear')}</Button>} />
        ) : (
          <div className={s.ltsWrap}>
            <div role="grid" aria-label={t('lt.gridAria')} className={s.ltsGrid}>
              <div role="row" className={s.ltsHead}>
                <div role="columnheader" className={s.ltsCode}>{t('lt.colSvc')}</div>
                {LTS_WEEKS.slice(0, nW).map(([k, d], i) => (
                  <div key={k} role="columnheader" className={`${s.ltsWk} ${i === 0 ? s.ltsNow : ''}`}>
                    <span>{i ? k : t('lt.thisWk', { k })}</span>
                    <div>{t('lt.from', { d })}</div>
                  </div>
                ))}
              </div>
              {shown.map((v) => (
                <div key={v.code} role="row" className={`${s.ltsRow} ${dens === 'gon' ? s.gon : ''}`}>
                  <div role="rowheader" className={s.ltsCode}>
                    <button type="button" className={s.ltsOpen} aria-label={t('lt.openSvc', { code: v.code })} onClick={() => onView('svc')}>{v.code}</button>
                    <span>{v.vn}</span>
                  </div>
                  {cellsOf(v).slice(0, nW).map((raw, i) => {
                    const c = cellView(v.code, raw, i);
                    const on = sel?.code === v.code && sel.i === i;
                    return (
                      <button key={i} type="button" role="gridcell" aria-selected={on} aria-label={c.al} title={c.al} className={`${s.ltsCell} ${s[`lc_${c.kind}`]} ${i === 0 && c.kind === 'ok' ? s.lc_now : ''}`} onClick={() => setSel(on ? null : { code: v.code, i })}>
                        <span>{c.flag && <i className={s.lcFlag} aria-hidden="true" />}{c.n}</span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
        {detail && (
          <div role="region" aria-label={t('lt.detAria')} className={s.ltsDetail}>
            <div className={s.ltsDetailMain}>
              <div className={s.ltsDetailHead}><b>{detail.title}</b><span className={`${s.stChip} ${detail.status[1]}`}>{detail.status[0]}</span></div>
              <div className={s.ltsDetailSub}>{detail.sub}</div>
              <div className={s.ltsInfo}>
                {detail.info.map(([k, v]) => (
                  <div key={k}><div>{k}</div><b>{v}</b></div>
                ))}
              </div>
            </div>
            <div className={s.ltsActs}>
              {detail.acts.map((a) => (
                <button key={a.k} type="button" className={s.ltsAct} onClick={a.go}><a.icon size={16} aria-hidden="true" />{a.k}</button>
              ))}
              <button type="button" className={`${s.ltsAct} ${s.ltsActGhost}`} onClick={() => setSel(null)}><IconX size={16} aria-hidden="true" />{t('lt.close')}</button>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
